import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnChanges,
  SimpleChanges,
  input,
  output,
  viewChild,
} from '@angular/core';

import { AppSharedModule } from 'app/app-shared.module';
import {
  ObjectLabel,
  ObjectLabelObjectType,
  ItemLabel,
} from 'app/providers/storage-api/object-label.service';
import { ItemLabelWrapper } from 'app/providers/storage-api/object-label.service';
import { EditLabelPopoutComponent } from '../edit-label-popout/edit-label-popout.component';
import { ItemLabelComponent } from '../item-label/item-label.component';

@Component({
  selector: 'app-item-labels',
  imports: [AppSharedModule, ItemLabelComponent, EditLabelPopoutComponent],
  templateUrl: './item-labels.component.html',
  styleUrl: './item-labels.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemLabelsComponent implements OnChanges {
  item = input<ItemLabelWrapper>();
  isReadOnly = input<boolean>();

  labelUpdated = output<Partial<ItemLabel>>();
  labelApplied = output<Partial<ItemLabel>>();
  labelRemoved = output<string>();

  addLabelButton = viewChild<ElementRef>('addLabelButton');

  objectType: ObjectLabelObjectType | string;

  itemLabels: Partial<ItemLabel>[];
  newLabel: Partial<ItemLabel>;
  updatedLabel: ItemLabel;
  originalLabelText: string;

  isLabelEditorVisible;

  ngOnChanges(changes: SimpleChanges): void {
    this.objectType = this.itemTypeToObjectType(changes.item.currentValue);
    this.itemLabels = [...this.item().labels];
  }

  private itemTypeToObjectType(item: ItemLabelWrapper): ObjectLabelObjectType | string {
    switch (item.type) {
      case 'group':
        return 'analysisGroup';
      case 'analysis':
        if (item.subType === 'kda') {
          return 'driverAnalysis';
        }
        return 'analysis';
      default:
        return item.type;
    }
  }

  onAddLabelClicked() {
    this.newLabel = {
      label: '',
      type: 'user',
    };
    this.isLabelEditorVisible = true;
  }

  onLabelClicked($event: ItemLabel) {
    if (this.isReadOnly() || $event.type === 'system') {
      return;
    }

    this.updatedLabel = $event;
    this.originalLabelText = $event.label;
    this.isLabelEditorVisible = true;
  }

  onLabelTextUpdating($event) {
    if (this.newLabel) {
      this.newLabel = {
        ...this.newLabel,
        label: $event,
      };
      return;
    }

    this.updatedLabel.label = $event;
    const labelIdx = this.itemLabels.findIndex((l) => l.id === this.updatedLabel.id);
    this.itemLabels.splice(labelIdx, 1, { ...this.updatedLabel });
  }

  onLabelUpdated($event: ObjectLabel) {
    const itemLabel: Partial<ItemLabel> = { ...$event, type: 'user' };

    this.labelUpdated.emit(itemLabel);

    const labelIdx = this.itemLabels.findIndex((l) => l.id === $event.id);
    this.itemLabels.splice(labelIdx, 1, itemLabel);
    this.itemLabels = [...this.itemLabels];

    this.onLabelEditorClosed($event);
  }

  onLabelApplied($event: ObjectLabel) {
    const itemLabel: Partial<ItemLabel> = { ...$event, type: 'user' };

    this.labelApplied.emit(itemLabel);

    this.itemLabels = [...this.itemLabels, itemLabel];

    this.onLabelEditorClosed($event);
  }

  onLabelRemoved($event: { labelId: string; objectId: string }) {
    this.labelRemoved.emit($event.labelId);

    this.itemLabels = this.itemLabels.filter((l) => l.id !== $event.labelId);

    this.onLabelEditorClosed($event);
  }

  onLabelEditorClosed(result?) {
    if (!result && this.updatedLabel) {
      const labelIdx = this.itemLabels.findIndex((l) => l.id === this.updatedLabel.id);
      this.itemLabels.splice(labelIdx, 1, {
        ...this.updatedLabel,
        label: this.originalLabelText,
      });
    }

    this.newLabel = undefined;
    this.updatedLabel = undefined;
    this.isLabelEditorVisible = false;
  }

  labelTracker(label: ItemLabel): string {
    return label.id ?? label.type + ':' + label.label;
  }
}
